/**
 * Hides B2B-only variant option values from the cart line items (mini-cart drawer
 * and cart page) for retail/guest shoppers — the cart-side counterpart of the PDP
 * gate (see bbd-b2b-options.js).
 *
 * The section only renders this element for non-B2B shoppers, so its presence means
 * "hide the listed options". Which options are B2B-only comes from the shop metafield
 * custom.b2b_only_variant_options, passed in via data-hidden-options.
 *
 * The cart re-renders on cart updates, so a debounced MutationObserver re-applies the
 * filter to any freshly rendered cart line. Checkout is Shopify-hosted and cannot be
 * changed from the theme.
 */
class BbdB2bCartOptions extends HTMLElement {
  connectedCallback() {
    this.hiddenNames = (this.dataset.hiddenOptions || '')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);
    if (this.hiddenNames.length === 0) return;
    this.apply();
    this.#observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  // Idempotent: each option list is processed once (marked data-b2b-done); cart
  // re-renders replace the list with a fresh, unmarked one that gets reprocessed.
  apply = () => {
    const lists = document.querySelectorAll('.cart-items__variants:not([data-b2b-done])');
    for (const list of lists) {
      let hidAny = false;
      for (const variant of list.querySelectorAll('.cart-items__variant')) {
        const label = variant.querySelector('dt');
        const name = (label?.textContent || '').replace(/:\s*$/, '').trim().toLowerCase();
        if (!this.hiddenNames.includes(name)) continue;
        variant.hidden = true;
        variant.setAttribute('data-b2b-hidden', 'true');
        hidAny = true;
      }
      if (hidAny) this.#stripTrailingSeparator(list);
      list.setAttribute('data-b2b-done', 'true');
    }
  };

  // The option separator (", ") lives at the end of each non-last value; after hiding
  // trailing options, clean the dangling separator off the last visible value.
  #stripTrailingSeparator(list) {
    const visible = Array.from(list.querySelectorAll('.cart-items__variant')).filter(
      (variant) => !variant.hasAttribute('data-b2b-hidden')
    );
    const dd = visible[visible.length - 1]?.querySelector('dd');
    if (dd) dd.textContent = dd.textContent.replace(/[\s,]+$/u, '');
  }

  #observe() {
    this.observer = new MutationObserver(() => {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.apply();
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (!customElements.get('bbd-b2b-cart-options')) {
  customElements.define('bbd-b2b-cart-options', BbdB2bCartOptions);
}
